import { Divider, Grid, ListItem } from "@mui/material";
import { canFieldBeHidden } from "../../utils/CreateReportUtils";
import { padding2, textColor, width } from "../Styles";
import { AutocompleteInput, SelectInput, TextInput } from "ra-ui-materialui";
import { useFormContext, useWatch } from 'react-hook-form';
import { isPersonaTemplateEnabled, isPersonaTemplateEnabledForBundle, prepareCountryPossibleValues, requiredValidation, validate } from "../../utils/util";
import { useEffect } from "react";
import isEmpty from "lodash/isEmpty"
import { TooltipDescription } from "../common/TooltipDescription";
import CustomDateInput from "../common/CustomDateInput";
import momentDate from "../../utils/moment-date";
import { showAdditionalIdentityInputs, showInputsForDbChecks } from "../../mappers/IdentityTemplate";
import { COUNTRIES_WITH_CODES } from "../CountriesConstants";
import { CommonInputForCreateReportGrid } from "../common/TemplateInputsForCreateReportGrid";
import { useLocation } from "react-router";
import { extractQueryParamsFromURL } from "../../mappers/Report";
import moment from "moment-timezone";

export const PersonaTemplateComponent = (props) => {  

  const { personaTemplateField, emailAddressField, personaTemplates, selectedTemplateId, birthDateField, countryField,
    phoneNumberField, addressStreet1Field, addressStreet2Field, addressCityField, addressSubdivisionField, addressPostalCodeField } = props;

  const possibleOptions = personaTemplateField?.possibleValues && personaTemplates?.filter(pt => personaTemplateField.possibleValues.includes(pt.name))
  const defaultValue = personaTemplates?.filter(pt => pt.defaultForClient)[0]?.id;
  const defaultValueFromTemplate = personaTemplateField?.defaultValue && personaTemplates?.filter(pt => !personaTemplateField.defaultValue.localeCompare(pt.name))[0]?.id;
  const values = useWatch();
  const form = useFormContext();
  const location = useLocation();
  const showIdentityInputs = showAdditionalIdentityInputs(personaTemplates, values?.identityVerificationTemplate);
  const showInputsForDbCheck = showInputsForDbChecks(personaTemplates, values?.identityVerificationTemplate);  
  const birthDateDefaultValue = birthDateField?.defaultValue ? momentDate(birthDateField.defaultValue).format("YYYY-MM-DD") : null;
  const possibleCountryOptions = prepareCountryPossibleValues(countryField?.possibleValues);
  const isIdentityTemplateEnabled = isPersonaTemplateEnabled(values.reportType) || isPersonaTemplateEnabledForBundle(values.multiReportType);

  const birthDateFromUrl = extractQueryParamsFromURL(location?.search, 'birthDate');
  const resolvedBirthDateFromUrl = birthDateFromUrl && momentDate(birthDateFromUrl).isValid()
    ? moment(birthDateFromUrl, 'DD/MM/YYYY').format("YYYY-MM-DD")
    : null;    
  const resolvedBirthDateDefault = resolvedBirthDateFromUrl ?? birthDateDefaultValue;
    
  const resolvedCountryDefault = extractQueryParamsFromURL(location?.search, 'country') ?? countryField?.defaultValue ?? null;  
  const resolvedPhoneNumberDefault = extractQueryParamsFromURL(location?.search, 'phoneNumber') ?? phoneNumberField?.defaultValue;
  const resolvedAddressStreet1Default = extractQueryParamsFromURL(location?.search, 'identityAddressStreet1') ?? addressStreet1Field?.defaultValue;
  const resolvedAddressStreet2Default = extractQueryParamsFromURL(location?.search, 'identityAddressStreet2') ?? addressStreet2Field?.defaultValue;
  const resolvedAddressCityDefault = extractQueryParamsFromURL(location?.search, 'identityAddressCity') ?? addressCityField?.defaultValue;
  const resolvedAddressSubdivisionDefault = extractQueryParamsFromURL(location?.search, 'identityAddressSubdivision') ?? addressSubdivisionField?.defaultValue;
  const resolvedAddressPostalCodeDefault = extractQueryParamsFromURL(location?.search, 'identityAddressPostalCode') ?? addressPostalCodeField?.defaultValue;

  useEffect(() => {//crs-hook
    isIdentityTemplateEnabled
      ? form.setValue("identityVerificationTemplate", defaultValueFromTemplate ? defaultValueFromTemplate : defaultValue)
      : form.setValue("identityVerificationTemplate", undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps      
  }, [
    isIdentityTemplateEnabled,
    values.reportType,
    selectedTemplateId,
    defaultValue,
    defaultValueFromTemplate,
  ]);

  useEffect(() => {//crs-hook
    if (isIdentityTemplateEnabled && showIdentityInputs) {
      form.setValue("birthDate", resolvedBirthDateDefault);
      form.setValue("country", resolvedCountryDefault);
      if (showInputsForDbCheck) {
        form.setValue("identityAddressCity", resolvedAddressCityDefault);
        form.setValue("identityAddressStreet1", resolvedAddressStreet1Default);
        form.setValue("identityAddressStreet2", resolvedAddressStreet2Default);
        form.setValue("identityAddressSubdivision", resolvedAddressSubdivisionDefault);
        form.setValue("identityAddressPostalCode", resolvedAddressPostalCodeDefault);
        form.setValue("identityPhoneNumber", resolvedPhoneNumberDefault);
      } else {
        form.resetField("identityAddressCity");
        form.resetField("identityAddressStreet1");
        form.resetField("identityAddressStreet2");
        form.resetField("identityAddressSubdivision");
        form.resetField("identityAddressPostalCode");
        form.resetField("identityPhoneNumber");
      }
    } else {
      form.resetField("birthDate");
      form.setValue("country", null);
      form.resetField("identityAddressCity");
      form.resetField("identityAddressStreet1");
      form.resetField("identityAddressStreet2");
      form.resetField("identityAddressSubdivision");
      form.resetField("identityAddressPostalCode");
      form.resetField("identityPhoneNumber");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isIdentityTemplateEnabled,
    values.reportType,
    selectedTemplateId,
    birthDateDefaultValue,
    countryField?.defaultValue,
    addressCityField?.defaultValue,
    addressStreet1Field?.defaultValue,
    addressStreet2Field?.defaultValue,
    addressSubdivisionField?.defaultValue,
    addressPostalCodeField?.defaultValue,
    phoneNumberField?.defaultValue,
    showIdentityInputs,
    showInputsForDbCheck,
  ]);

  useEffect(() => {//crs-hook
    if (isEmpty(emailAddressField)) {
      form.setValue("emailAddress", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAddressField, selectedTemplateId]);

  useEffect(() => {//crs-hook
    const isIdentityReport = isPersonaTemplateEnabled(values.reportType) || isPersonaTemplateEnabledForBundle(values.multiReportType);
    const selectedIdentityTemplate = personaTemplates?.filter(template => template.id === values?.identityVerificationTemplate)[0];
    const isDirectIDTemplateSelected = isIdentityReport && selectedIdentityTemplate?.skipUserJourney === true;
    if (isDirectIDTemplateSelected) {
      form.setValue("isDirectIDIdentityTemplateSelected", true);
    } else {
      form.setValue("isDirectIDIdentityTemplateSelected", false);
    }
    if (isDirectIDTemplateSelected && isEmpty(values?.emailAddress)) {
      form.setValue("autoRegister", true);
    } else {
      form.setValue("autoRegister", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    personaTemplates,
    selectedTemplateId,
    values?.reportType,
    values?.multiReportType,
    values?.identityVerificationTemplate,
    values?.emailAddress
  ]);

  return( isIdentityTemplateEnabled &&
  <>
    <Grid sx={{
      ...canFieldBeHidden(
        personaTemplateField?.isHidden,
        true,
        defaultValue || defaultValueFromTemplate
      ) ? { display: "none" } : {}
    }}>
      <ListItem sx={padding2.y}>
        <Grid container alignItems="center">
          <Grid item xs={12} md={4} sx={textColor.gray}>
            {personaTemplateField?.displayName ? personaTemplateField.displayName : "Identity Verification Template"}
            <span style={{ ...textColor.red, marginLeft: "3px" }}>*</span>
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            <SelectInput
              id="reports-create-identity-template-select"
              source="identityVerificationTemplate"
              choices={!isEmpty(possibleOptions) ? possibleOptions : personaTemplates}
              label={personaTemplateField?.placeholder ? personaTemplateField.placeholder : "Choose an option"}
              variant="outlined"
              size="small"
              sx={{ ...width.fluid.w100, marginBottom: "-15px" }}                        
              defaultValue={defaultValueFromTemplate ? defaultValueFromTemplate : defaultValue}
              validate={requiredValidation}
              optionText={<TooltipDescription />}
            />
          </Grid>
        </Grid>
      </ListItem>
      <Divider light />
    </Grid>
    {
      //TODO :- Add validations for custom Date Input
      showIdentityInputs &&
      <Grid sx={{
        ...canFieldBeHidden(
          birthDateField?.isHidden,
          birthDateField?.required,
          birthDateDefaultValue
        ) ? { display: "none" } : {}
      }}>
        
        <ListItem sx={padding2.y}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={4} sx={textColor.gray}>
              {birthDateField?.displayName ? birthDateField.displayName : "Date of Birth"}
              {birthDateField?.required && <span style={{ ...textColor.red, marginLeft: "3px" }}>*</span>}
            </Grid>
            <Grid item xs={12} md={6} lg={5} sx={{ margin: "8px 0px" }}>
              <CustomDateInput
                source="birthDate"
                defaultValue={resolvedBirthDateDefault}
                placeholder={birthDateField?.placeholder}
                validate={birthDateField?.required ? validate : false}
                isClearable={!birthDateField?.required}
                maxDate={new Date()}
              />
            </Grid>
          </Grid>
        </ListItem>
        <Divider light />
      </Grid>
    }

    {showInputsForDbCheck &&
      <CommonInputForCreateReportGrid
        field={phoneNumberField}
        defaultDisplayName="Phone Number"
        input={
          <TextInput
            source="identityPhoneNumber"
            name="identityPhoneNumber"
            defaultValue={resolvedPhoneNumberDefault}
            variant="outlined"
            size="small"
            label={phoneNumberField?.placeholder ? phoneNumberField.placeholder : "Enter Phone Number"}
            validate={phoneNumberField?.required ? validate : false}
            allowEmpty={!phoneNumberField?.required}
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
          />
        }
      />
    }
    {showInputsForDbCheck &&
      <CommonInputForCreateReportGrid
        field={addressStreet1Field}
        defaultDisplayName="Address Street 1"
        input={
          <TextInput
            source="identityAddressStreet1"
            name="identityAddressStreet1"
            defaultValue={resolvedAddressStreet1Default}
            variant="outlined"
            size="small"
            label={addressStreet1Field?.placeholder ? addressStreet1Field.placeholder : "Enter Address Street 1"}
            validate={addressStreet1Field?.required ? validate : false}
            allowEmpty={!addressStreet1Field?.required}
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
          />
        }
      />
    }
    {showInputsForDbCheck &&
      <CommonInputForCreateReportGrid
        field={addressStreet2Field}
        defaultDisplayName="Address Street 2"
        input={
          <TextInput
            source="identityAddressStreet2"
            name="identityAddressStreet2"
            defaultValue={resolvedAddressStreet2Default}
            variant="outlined"
            size="small"
            label={addressStreet2Field?.placeholder ? addressStreet2Field.placeholder : "Enter Address Street 2"}
            validate={addressStreet2Field?.required ? validate : false}
            allowEmpty={!addressStreet2Field?.required}
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
          />
        }
      />
    }
    {showInputsForDbCheck &&
      <CommonInputForCreateReportGrid
        field={addressCityField}
        defaultDisplayName="Address City"
        input={
          <TextInput
            source="identityAddressCity"
            name="identityAddressCity"
            defaultValue={resolvedAddressCityDefault}
            variant="outlined"
            size="small"
            label={addressCityField?.placeholder ? addressCityField.placeholder : "Enter Address City"}
            validate={addressCityField?.required ? validate : false}
            allowEmpty={!addressCityField?.required}
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
          />
        }
      />
    }
    {
      showInputsForDbCheck &&
      <CommonInputForCreateReportGrid
        field={addressSubdivisionField}
        defaultDisplayName="Address Subdivision"
        input={
          <TextInput
            source="identityAddressSubdivision"
            name="identityAddressSubdivision"
            defaultValue={resolvedAddressSubdivisionDefault}
            variant="outlined"
            size="small"
            label={addressSubdivisionField?.placeholder ? addressSubdivisionField.placeholder : "Enter Address Subdivision"}
            validate={addressSubdivisionField?.required ? validate : false}
            allowEmpty={!addressSubdivisionField?.required}
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
          />
        }
      />
    }
    {showInputsForDbCheck &&
      <CommonInputForCreateReportGrid
        field={addressPostalCodeField}
        defaultDisplayName="Address Postal Code"
        input={
          <TextInput
            source="identityAddressPostalCode"
            name="identityAddressPostalCode"
            defaultValue={resolvedAddressPostalCodeDefault}
            variant="outlined"
            size="small"
            label={addressPostalCodeField?.placeholder ? addressPostalCodeField.placeholder : "Enter Address Postal Code"}
            validate={addressPostalCodeField?.required ? validate : false}
            allowEmpty={!addressPostalCodeField?.required}
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
          />
        }
      />
    }
    {showIdentityInputs &&
      <CommonInputForCreateReportGrid
        field={countryField}
        defaultDisplayName="Country"
        input={
          <AutocompleteInput
            id="reports-create-identity-country-autocomplete"
            sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
            source="country"
            name="country"
            defaultValue={resolvedCountryDefault}
            variant="outlined"
            size="small"
            label={countryField?.placeholder ? countryField.placeholder : "Choose an option"}
            choices={!isEmpty(possibleCountryOptions) ? possibleCountryOptions : COUNTRIES_WITH_CODES}
            validate={countryField?.required ? validate : false}
          />
        }
      />
    }
    
  </>
    );
  };